import {
  QueriedData,
  showErrorNotification,
  showSuccessNotification,
} from '../assets/useAssets';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Colaborator,
  ColaboratorWriteModel,
  createColaborator,
  deleteColaborator,
  getAllColaborators,
  inviteColaborator,
  updateColaborator,
} from 'src/api/colaborators';

export interface UseColaboratorsResult {
  colaboratorsQuery: QueriedData<Colaborator[]>;
  createColaborator: (colaborator: ColaboratorWriteModel) => void;
  updateColaborator: (data: {
    id: string;
    colaborator: ColaboratorWriteModel;
  }) => void;
  archiveColaborator: (colaboratorID: string) => void;
  inviteColaborator: (id: string) => void;
}

function useCreateColaborator() {
  const queryClient = useQueryClient();
  const { mutate: create } = useMutation(
    (colaborator: ColaboratorWriteModel) => createColaborator(colaborator),
    {
      onSuccess: () => {
        showSuccessNotification('Colaborador criado com sucesso');
        queryClient.invalidateQueries(['colaborators']);
      },
      onError: () => {
        showErrorNotification('Ocorreu um erro inesperado');
      },
    },
  );
  return create;
}

export function useColaborators(): UseColaboratorsResult {
  const query = useQuery({
    queryFn: async () => getAllColaborators(),
    queryKey: ['colaborators'],
  });

  const create = useCreateColaborator();
  const queryClient = useQueryClient();
  const { mutate: archive } = useMutation(
    (colaboratorID: string) => deleteColaborator(colaboratorID),
    {
      onSuccess: () => {
        showSuccessNotification('Colaborador arquivado com sucesso');
        queryClient.invalidateQueries(['colaborators']);
      },
      onError: () => showErrorNotification('Ocorreu um erro inseperado'),
    },
  );
  const updateColaborator = useUpdateColaborator();
  const inviteColaborator = useInviteColaborator();
  return {
    colaboratorsQuery: query,
    createColaborator: create,
    archiveColaborator: archive,
    updateColaborator,
    inviteColaborator,
  };
}

export function useUpdateColaborator() {
  const queryClient = useQueryClient();

  const { mutate: update } = useMutation(
    ({ id, colaborator }: { id: string; colaborator: ColaboratorWriteModel }) =>
      updateColaborator(id, colaborator),
    {
      onSuccess: (_, { id }) => {
        showSuccessNotification('Colaborador atualizado com sucesso');
        queryClient.invalidateQueries(['colaborators', id]);
        queryClient.invalidateQueries(['colaborators']);
      },
      onError: () => {
        showErrorNotification('Ocorreu um erro inesperado');
      },
    },
  );
  return update;
}

export function useInviteColaborator() {
  const queryClient = useQueryClient();

  const { mutate: inviteColaboratorMutation } = useMutation({
    mutationFn: (id: string) => inviteColaborator(id),
    onSuccess: (_, id) => {
      showSuccessNotification('Será enviado um convite ao colaborador');
      queryClient.invalidateQueries(['colaborators', id]);
      queryClient.invalidateQueries(['colaborators']);
    },
    onError: () => {
      showErrorNotification('Ocorreu um erro inesperado');
    },
  });
  return inviteColaboratorMutation;
}
